import React, { useCallback } from 'react';

import { OidcClient, OidcProvider } from '@axa-fr/react-oidc';
import { LoginCallback, LoginError, oidcConfig } from '@fly/features/auth';
import { useSessionLost } from '@fly/shared-hooks';
import { FlyLoader } from '@fly/shared-ui';
import log from 'loglevel';

import LoginSuccessHandler from './LoginSuccessHandler';

const { eventNames } = OidcClient;

/**
 * Wraps @axa-fr/react-oidc's OidcProvider and maps OIDC events to Redux
 * session-lost state.
 *
 * Two subscriptions fire on token-refresh errors:
 *   onSessionLost  — () => void, no event data available. Sets Redux flag.
 *   onEvent        — receives (config, eventName, data). Logs diagnostics.
 *
 * Session recovery: when silent renew succeeds, token_renewed clears the flag.
 *
 * loginCallbackAsync_error workarounds handle known Keycloak edge cases:
 *   "State not valid"          → stale PKCE state after back-navigation → redirect /
 *   "authentication_expired"   → KC session expired mid-flow → redirect /
 *   "login_required"           → KC requires re-auth → reload triggers new login
 * These may be removed once we migrate to service-worker token storage.
 */
export default function OidcProviderWithSessionLost({
  children,
}: {
  children: React.ReactNode;
}) {
  const { setIsSessionLost } = useSessionLost();
  const handleSessionLost = useCallback(() => {
    log.warn(
      'OIDC: Session lost — token refresh or sync failed. See preceding onEvent log for event name and error detail.'
    );
    setIsSessionLost(true);
  }, [setIsSessionLost]);

  const handleOidcEvent = useCallback(
    (_configuration: string, name: string, data: Record<string, unknown>) => {
      const message =
        typeof data?.message === 'string' ? data.message : undefined;

      if (
        name === eventNames.refreshTokensAsync_error ||
        name === eventNames.syncTokensAsync_error
      ) {
        log.warn('OIDC session lost:', name, message ?? data);
      }

      if (name === eventNames.token_renewed) {
        setIsSessionLost(false);
      }

      if (name === eventNames.loginCallbackAsync_error) {
        if (message?.includes('State not valid')) {
          log.info('OIDC: State not valid');
          window.location.assign('/');
        } else if (
          message ===
          'Error from OIDC server: temporarily_unavailable - authentication_expired'
        ) {
          log.info('OIDC: authentication_expired');
          window.location.assign('/');
        } else if (message?.includes('login_required - undefined')) {
          log.info('OIDC: login_required');
          window.location.reload();
        }
      }
    },
    [setIsSessionLost]
  );

  return (
    <OidcProvider
      configuration={oidcConfig}
      authenticatingErrorComponent={LoginError}
      loadingComponent={FlyLoader}
      authenticatingComponent={FlyLoader}
      callbackSuccessComponent={LoginCallback}
      onSessionLost={handleSessionLost}
      onEvent={handleOidcEvent}
    >
      {children}
      <LoginSuccessHandler />
    </OidcProvider>
  );
}
